/**
 * lx-page-home —— 首页（会话列表）
 *
 * 两种状态，由是否有会话数据决定：
 *   空 → 空态引导（含「新建联系人」这条路）
 *   非空 → 真实会话列表
 *
 * 状态的切换完全由数据驱动：订阅 session:list-changed 后重新判断，
 * 所以在 AI 里说「给王二发条消息」，首页会自动从空态变成列表。
 */

import { LxElement, define } from '@/core/base-element';
import { sessionsRepo } from '@/data/repo.sessions';
import type { LxEmptyState } from './lx-empty-state';
import './lx-empty-state';
import './lx-session-list';

export class LxPageHome extends LxElement {
  private _count = 0;
  private _loading = true;

  protected styles(): string {
    return `
      :host {
        display: flex;
        flex-direction: column;
        width: 100%;
        min-height: 0;
      }

      lx-session-list,
      lx-empty-state {
        flex: 1 1 auto;
        min-height: 0;
      }
    `;
  }

  protected render(): string {
    if (this._loading) return '';
    return this._count > 0
      ? '<lx-session-list></lx-session-list>'
      : '<lx-empty-state></lx-empty-state>';
  }

  protected onMounted(): void {
    const empty = this.$('lx-empty-state') as LxEmptyState | null;
    if (!empty) return;

    empty.options = {
      title: '还没有会话',
      description: '灵讯里的一切都能用一句话完成。先建个联系人，或者让 AI 帮你建。',
      actionLabel: '新建联系人',
      hint: '新建联系人 王二',
    };

    // 按钮 → 打开表单；提示语里那条命令则由用户自己打给 AI
    empty.addEventListener('empty-action', () => {
      this.emit('ui:contact-editor-open', {});
    });
  }

  private async load(): Promise<void> {
    const list = await sessionsRepo.listWithContact();
    const count = list.length;
    const wasLoading = this._loading;

    this._loading = false;
    // 数量没变时不重渲染，避免列表滚动位置被打断
    if (count === this._count && !wasLoading) return;

    this._count = count;
    this.update();
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
    this.subscribe('session:list-changed', () => void this.load());
    this.subscribe('contact:list-changed', () => void this.load());
  }
}

define('lx-page-home', LxPageHome);
